import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Paper, Typography, Box } from '@mui/material';
import TableSkeleton from '../common/TableSkeleton';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StartIcon from '@mui/icons-material/PlayCircleOutline';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import CloseIcon from '@mui/icons-material/Close';
import { taskServices } from '../../services/taskServices';
import moment from 'moment';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import ConfirmationDialog from '../common/ConfirmationDialog';
import StopIcon from '@mui/icons-material/Stop';
import { useDispatch, useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { fetchUserDetails } from '../redux/userDetailsSlice';
import { useSnackbar } from '../../utils/useSnackbar';
import PageHeader from '../ui/PageHeader';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const ExtraTasks = () => {
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchExtraTask, setSearchExtraTask] = useState('');
  const [debouncedSearchExtraTask, setDebouncedSearchExtraTask] = useState(searchExtraTask);
  const [filterExtraTask, setFilterExtraTask] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [searchActiveExtraTask, setSearchActiveExtraTask] = useState('');
  const [debouncedSearchActiveExtraTask, setDebouncedSearchActiveExtraTask] = useState(searchActiveExtraTask);
  const [filterActiveExtraTask, setFilterActiveExtraTask] = useState('');
  const [activeSortModel, setActiveSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const navigationUrl = "/home/extra-task"
  const navigationTaskTimeTrackUrl = "/home/task-time-track"
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const { data: userData, loading1, error } = useSelector(state => state.userDetails);
  const pageSizeOptions = [5, 10, 15];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [roles, setRoles] = useState([]);
  const isAdmin = Array.isArray(roles) && (roles.includes('ADMIN') || roles.includes('SUPER_ADMIN'));
  const isManageJob = Array.isArray(roles) && (roles.includes('SUPER_ADMIN') || roles.includes('ADMIN') || roles.includes('SUB_ADMIN') || roles.includes('MANAGE_JOBS'));
  const dispatch = useDispatch();
  const [taskToStop, setTaskToStop] = useState(null);
  const [description, setDescription] = useState('');
  const [isStopping, setIsStopping] = useState(false);
  const [activeExtraTask, setActiveExtraTask] = useState([]);
  const [activeExtraRowCount, setActiveExtraRowCount] = useState(0);
  const [activeExtraPaginationModel, setActiveExtraPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [extraStartTimes, setExtraStartTimes] = useState({});
  const [tick, setTick] = useState(0);
  const [buttonsState, setButtonsState] = useState({
    startDisabled: false
  });
  const isSearchFirstRender = useRef(true);
  const isActiveSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);
  const isActiveSortFirstRender = useRef(true);
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (activeExtraTask && userData?.id) {
      const isUserTaskRunning = activeExtraTask?.find(task => task?.assignedBy?.id === userData.id);

      setButtonsState({
        startDisabled: isUserTaskRunning
      });
    }
  }, [activeExtraTask, userData]);

  useEffect(() => {
    if (accessToken && !userData.length) {
      const decodedToken = jwtDecode(accessToken);
      const userEmail = decodedToken.sub;
      let extractedRoles = [];

      if (Array.isArray(decodedToken.roles)) {
        extractedRoles = decodedToken.roles;
      } else if (typeof decodedToken.roles === "string") {
        extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
      }

      setRoles(extractedRoles);
      dispatch(fetchUserDetails(userEmail));
    }
  }, [accessToken, dispatch, userData]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const taskField = sortModel?.[0]?.field || 'updatedAt';
      const taskCol = columns.find(c => c.field === taskField);
      const tasksSortField = taskCol?.sortField || taskField;
      const tasksSortDir = sortModel?.[0]?.sort || 'desc';

      const activeTaskField = activeSortModel?.[0]?.field || 'updatedAt';
      const activeTaskCol = activeColumns.find(c => c.field === activeTaskField);
      const activeSortField = activeTaskCol?.sortField || activeTaskField;
      const activeSortDir = activeSortModel?.[0]?.sort || 'desc';

      const res = await taskServices.getAllExtraTasks(debouncedSearchExtraTask, filterExtraTask, paginationModel.page, paginationModel.pageSize, activeExtraPaginationModel.page, activeExtraPaginationModel.pageSize, debouncedSearchActiveExtraTask, filterActiveExtraTask, tasksSortField, tasksSortDir, activeSortField, activeSortDir);

      if (res.extraTasks) {
        setTasksData(res.extraTasks.content || []);
        setRowCount(res.extraTasks.totalElements || 0);
      }

      if (res.activeExtraTasks) {
        const active = res.activeExtraTasks.content || [];
        setActiveExtraTask(active);
        setActiveExtraRowCount(res.activeExtraTasks.totalElements || 0);

        const startMap = {};
        active.forEach((task) => {
          if (task.startTime) {
            const parsed = moment(task.startTime, "DD-MM-YYYY hh:mm:ss A", true);
            if (parsed.isValid()) {
              startMap[task.id] = parsed.valueOf();
            }
          }
        });
        setExtraStartTimes(startMap);
        setRowCount(res.extraTasks.totalElements);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setTasksData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
      }
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    const interval = setInterval(() => setTick(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const extraLiveDurationsMemo = useMemo(() => {
    if (!extraStartTimes) return {};

    const updated = {};
    Object.entries(extraStartTimes).forEach(([id, startTime]) => {
      const start = Number(startTime);
      updated[id] = Math.max(0, Math.floor((Date.now() - start) / 1000)); // duration in seconds
    });

    return updated;
  }, [extraStartTimes, tick]);

  const formatHMS = totalSeconds => {
    const days = Math.floor(totalSeconds / 86400);
    const remAfterDays = totalSeconds % 86400;
    const hours = Math.floor(remAfterDays / 3600);
    const minutes = Math.floor((remAfterDays % 3600) / 60);
    if (days > 0) {
      return `${days}d:${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m`;
    } else {
      const secs = remAfterDays % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken, paginationModel, debouncedSearchExtraTask, filterExtraTask, snackbarMessage, activeExtraPaginationModel, debouncedSearchActiveExtraTask, filterActiveExtraTask, sortModel, activeSortModel]);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    const message = localStorage.getItem("successMessage");
    if (message) {
      showSnackbar(message, 'success');
      setSnackbarMessage(message);
      localStorage.removeItem("successMessage");
    }
  }, []);

  const handleAddTask = () => {
    if (accessToken) {
      navigate(navigationUrl);
    }
  };

  const handleEditTask = (task) => {
    if (accessToken) {
      navigate(navigationUrl, { state: { taskData: task } });
    }
  };

  const handleTaskTimeTrack = (task) => {
    if (accessToken) {
      navigate(navigationTaskTimeTrackUrl, { state: { taskData: task } });
    }
  };

  const handleDeleteTask = (taskId) => {
    setTaskToDelete(taskId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTaskToDelete(null);
  };

  const confirmDelete = () => {
    if (taskToDelete) {
      taskServices.deleteTaskData(taskToDelete)
        .then(() => {
          setTasksData((prevData) => prevData.filter(task => task.id !== taskToDelete));
          showSnackbar('Task deleted successfully!', 'success');
          setSnackbarMessage('Task deleted successfully!');
        })
        .catch((error) => {
          showSnackbar('Error deleting task data', 'error');
          setSnackbarMessage('Error deleting task data');
        });
    }
    handleCloseDialog();
  };

  const handleStartStopTime = (taskId, type) => {

    if (type === 'end') {
      setTaskToStop(taskId);
      setOpenDescriptionDialog(true);

    } else {
      if (type === 'stop') {
        setIsStopping(true);
      }

      const data = { taskId, type, description };

      if ((type === 'start') || (type === 'stop' && description.length > 0)) {
        setIsStopping(false);

        taskServices.postStartStopTask(data)
          .then((response) => {
            showSnackbar(`Task ${type} time updated successfully!`, 'success');
            setSnackbarMessage(`Task ${type} time updated successfully for ${taskId} task!`);
            if (type === 'start') {
              setButtonsState({
                startDisabled: true,
              });
            }
            if (type === 'stop') {
              setTaskToStop(null);
              setDescription('');
              setOpenDescriptionDialog(false);
              setButtonsState({
                startDisabled: false,
              });

            }
          }).catch((error) => {
            const errorMsg = error?.response?.data || "Something went wrong";
            showSnackbar(errorMsg, 'error');
            setSnackbarMessage(errorMsg);
            setIsStopping(false);
          });
      }
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);

    if (e.target.value.length > 0) {
      setIsStopping(false);
    }
  };

  const parseDate = (dateStr) => {
    // Use moment to parse the date in the given format
    const parsedDate = moment(dateStr, "DD-MM-YYYY hh:mm:ss A");

    // Check if the parsed date is valid
    if (!parsedDate.isValid()) {
      console.error("Invalid date format:", dateStr);
      return new Date(NaN);
    }

    // Return the parsed date as a JavaScript Date object
    return parsedDate.toDate();
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '';

    const start = parseDate(startTime);
    const end = parseDate(endTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid date";
    }

    // Calculate the duration in milliseconds
    const durationInMillis = end.getTime() - start.getTime();

    if (durationInMillis < 0) {
      return "End time cannot be earlier than start time";
    }

    const durationInSeconds = Math.floor(durationInMillis / 1000);
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    return (
      <Tooltip title={`${hours}h ${minutes}m ${seconds}s`}>
        <span>{`${hours}h ${minutes}m ${seconds}s`}</span>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchExtraTask.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchExtraTask(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchExtraTask]);

  useEffect(() => {
    if (isActiveSearchFirstRender.current) {
      isActiveSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchActiveExtraTask.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchActiveExtraTask(value);
        setActiveExtraPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchActiveExtraTask]);

  // Columns for the task table
  const columns = useMemo(() => [
    {
      field: 'title', headerName: t('task.title'), sortField: 'title', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'startTime', headerName: "Login Date Time", sortField: 'startTime', flex: 2, minWidth: isMobile ? 180 : 0, renderCell: (params) => {
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      }
    },
    {
      field: 'endTime', headerName: "Logout Date Time", sortField: 'endTime', flex: 2, minWidth: isMobile ? 180 : 0, renderCell: (params) => {
        <Tooltip title={params.value}><span >{params.value}</span></Tooltip>
      }
    },
    {
      field: 'duration',
      headerName: t('task.duration'),
      flex: 1,
      minWidth: isMobile ? 120 : 0,
      renderCell: (params) => {
        const { startTime, endTime } = params.row;
        return calculateDuration(startTime, endTime);
      },
    },
    {
      field: 'taskOwner', headerName: 'Task Owner', sortField: 'assignedBy.firstName', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => {
        const ownerFirstname = params.row.assignedUserFirstName;
        const ownerLastname = params.row.assignedUserLastName;
        return (
          <Tooltip title={ownerFirstname + ' ' + ownerLastname}><span>{ownerFirstname + ' ' + ownerLastname}</span></Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('task.actions'),
      flex: 2,
      minWidth: isMobile ? 180 : 0,
      pinned: 'right',
      renderCell: (params) => (
        <div>
          <Tooltip title={buttonsState.startDisabled ? "Already another task is running" : "Start Task"}>
            <span>
              <IconButton
                color="primary"
                onClick={() => handleStartStopTime(params.row.id, "start")}
                disabled={buttonsState.startDisabled || userData?.id !== params?.row?.assignedUserId}
                className="icon-button"
              >
                <StartIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            color="primary"
            onClick={() => handleTaskTimeTrack(params.row)}
            className="icon-button"
          >
            <AccessAlarmIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleEditTask(params.row)}
            className="icon-button"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {isManageJob && (
            <IconButton
              color="secondary"
              onClick={() => handleDeleteTask(params.row.id)}
              className="icon-button"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      ),
    },
  ], [isMobile, t, buttonsState.startDisabled, userData?.id, isManageJob]);

  const activeColumns = useMemo(() => [
    { field: 'title', headerName: 'Task Title', sortField: 'title', flex: 1, minWidth: isMobile ? 150 : 0 },
    {
      field: 'taskOwner',
      headerName: 'Task Owner',
      sortField: 'assignedBy.firstName',
      flex: 1,
      minWidth: isMobile ? 150 : 0,
      renderCell: (params) =>
        `${params.row.assignedUserFirstName || ''} ${params.row.assignedUserLastName || ''}`,
    },
    {
      field: 'runningTime',
      headerName: 'Running Time',
      flex: 1,
      minWidth: isMobile ? 120 : 0,
      renderCell: (params) =>
        extraLiveDurationsMemo[params.row.id] != null
          ? formatHMS(extraLiveDurationsMemo[params.row.id])
          : '00:00:00',
    },
    {
      field: 'action',
      headerName: 'Action',
      sortable: false,
      flex: 0.5,
      minWidth: isMobile ? 80 : 0,
      renderCell: (params) => {
        const canStop = isAdmin || userData?.id === params.row?.assignedUserId;

        return (
          <IconButton
            color="error"
            onClick={() => handleStartStopTime(params.row.id, 'end')}
            className="icon-button"
            disabled={!canStop}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        );
      },
    }
  ], [isMobile, extraLiveDurationsMemo, isAdmin, userData?.id]);

  const filteredDataMemo = useMemo(() => {
    if (!tasksData) return [];

    const filter = (task, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (task?.title?.toLowerCase().includes(lowerSearch)) ||
        (task.assignedUserFirstName && task.assignedUserFirstName.toLowerCase().includes(lowerSearch)) ||
        (task.assignedUserLastName && task.assignedUserLastName.toLowerCase().includes(lowerSearch)) ||
        `${task.assignedUserFirstName} ${task.assignedUserLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    return tasksData
      .filter(task => filter(task, searchExtraTask) && filter(task, filterExtraTask))
      .map(task => ({
        ...task,
        id: task?.id,
        titles: task?.title,
        taskOwner: task?.assignedUserFirstName
      }));
  }, [tasksData, searchExtraTask, filterExtraTask]);

  const filteredActiveTaskMemo = useMemo(() => {
    if (!activeExtraTask) return [];

    const filter = (task, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (task?.title?.toLowerCase().includes(lowerSearch)) ||
        (task?.assignedUserFirstName?.toLowerCase().includes(lowerSearch)) ||
        (task?.assignedUserLastName?.toLowerCase().includes(lowerSearch)) ||
        `${task?.assignedUserFirstName} ${task?.assignedUserLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    return activeExtraTask
      .filter(task => filter(task, searchActiveExtraTask) && filter(task, filterActiveExtraTask))
      .map(task => ({
        ...task,
        id: task?.id,
        title: task?.title,
        taskOwner: task?.assignedUserFirstName
      }));
  }, [activeExtraTask, searchActiveExtraTask, filterActiveExtraTask]);

  const extraTaskColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // startTime: false,
        // endTime: false,
        // duration: false,
        // taskOwner: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // endTime: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  const activeExtraTaskColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // taskOwner: false,
      };
    }

    return {};
  }, [isMobile]);

  return (
    <div className="data-table-container">
      <PageHeader title="Extra Tasks" />

      {/* Extra active task */}
      {filteredActiveTaskMemo && filteredActiveTaskMemo.length >= 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              mb: '0.625rem',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.125rem', mb: 0 }}>
              Active Extra Task
            </Typography>
            <Box className="search-container" sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <input
                type="text"
                placeholder="Type to search..."
                value={searchActiveExtraTask}
                onChange={(e) => setSearchActiveExtraTask(e.target.value)}
                className="search-input"
              />
            </Box>
          </Box>

          <Paper sx={{ marginBottom: '1.875rem' }}>
            <div className="data-table">
              <div className="data-grid-container" style={Object.keys(activeExtraTask).length <= 1 ? { height: '30vh' } : {}}>
                {loading ? (
                  <Box sx={{ height: '100%', overflow: 'hidden' }}>
                    <TableSkeleton rows={3} columns={6} />
                  </Box>
                ) : (
                  <DataGrid
                    loading={false}
                    rows={filteredActiveTaskMemo}
                    columns={activeColumns}
                    pagination
                    paginationMode="server"
                    filterMode="server"
                    onFilterModelChange={(newFilterModel) => {
                      const value = newFilterModel.items?.[0]?.value || '';
                      setFilterActiveExtraTask(value);
                    }}
                    sortingMode="server"
                    onSortModelChange={(newModel) => {
                      if (isActiveSortFirstRender.current) {
                        isActiveSortFirstRender.current = false;
                        if (!newModel?.length) return;
                      }
                      if (!newModel?.length && activeSortModel.length > 0) {
                        const currentField = activeSortModel[0].field;
                        const isColumnDefined = activeColumns.some(c => c.field === currentField);
                        if (!isColumnDefined) {
                          return;
                        }
                      }
                      setActiveSortModel(newModel);
                      setActiveExtraPaginationModel(prev => ({ ...prev, page: 0 }));
                    }}
                    sortModel={activeSortModel}
                    paginationModel={activeExtraPaginationModel}
                    rowCount={activeExtraRowCount}
                    onPaginationModelChange={setActiveExtraPaginationModel}
                    pageSizeOptions={pageSizeOptions}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    hideFooter={activeExtraRowCount <= 1}
                    columnVisibilityModel={activeExtraTaskColumnVisibilityModel}
                  />
                )}
              </div>
            </div>
          </Paper>
        </>

      )}

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleAddTask}
          className="add-button"
          startIcon={<AddIcon />}
        >
          {t('task.add')}
        </Button>

        {/* General Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchExtraTask}
            onChange={(e) => setSearchExtraTask(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setFilterExtraTask(value);
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);
                  if (!isColumnDefined) {
                    return;
                  }
                }
                setSortModel(newModel);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              disableSelectionOnClick
              columnVisibilityModel={extraTaskColumnVisibilityModel}
            />
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={confirmDelete}
          title="Delete Confirmation"
          message="Are you sure you want to delete this task?"
          cancelLabel="Cancel"
          confirmLabel="Delete"
          isDelete={true}
        />

        <Dialog
          open={openDescriptionDialog}
          onClose={() => setOpenDescriptionDialog(false)}
          slotProps={{ paper: { className: 'modern-dialog' } }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle className="modern-dialog-title">
            <span className="modern-dialog-title-text">Enter Description of Task</span>
            <IconButton
              aria-label="close"
              onClick={() => setOpenDescriptionDialog(false)}
              className="modern-dialog-close-btn"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="modern-dialog-content">
            <TextField
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter task description"
              variant="outlined"
              sx={{ mt: 1 }}
              required
              error={isStopping && description.length === 0}
              helperText={isStopping && description.length === 0 ? '*Description is required' : ''}
            />
          </DialogContent>
          <DialogActions className="modern-dialog-actions">
            <Button
              onClick={() => setOpenDescriptionDialog(false)}
              variant='outlined'
              color="inherit"
              sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleStartStopTime(taskToStop, 'stop')}
              color="primary"
              variant='contained'
              disabled={isStopping && description.length === 0}
              disableElevation
            >
              Stop
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  );
};

export default ExtraTasks;
